import React, { FC, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import { useMediaQuery } from 'react-responsive'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ParentalControlsFullIcon } from '@/atoms/Icons/ParentalControlsFullIcon'
import { SpeakerMaxIcon } from '@/atoms/Icons/SpeakerMaxIcon'
import { SpeakerMuteIcon } from '@/atoms/Icons/SpeakerMuteIcon'
import { TriangleArrowRight } from '@/atoms/Icons/TriangleArrowRight'
import { CaptionSM, LabelMD, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import {
  buildPreferenceTree,
  getMuteStatus,
  MuteStatus,
  PreferenceNode,
} from '@/services/ParentalControlsService/preference-tree'
import { SkipCategoryPreferenceItem } from '@/services/ParentalControlsService/useSkipCategoryPreferences'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface DarkMuteToggleProps {
  status: MuteStatus
  onToggle: () => void
}

const DarkMuteToggle: FC<DarkMuteToggleProps> = ({ status, onToggle }) => {
  const { t } = useTranslate('parental-controls')
  const isMuted = status === 'muted'

  return (
    <button type="button" className="flex w-10 shrink-0 flex-col items-center" onClick={onToggle}>
      {isMuted ? <SpeakerMuteIcon size={24} color="white" /> : <SpeakerMaxIcon size={24} color="white" />}
      <span
        className={classNames(
          'text-[10px] font-semibold uppercase leading-none text-oxide-bright',
          status !== 'mixed' && 'invisible',
        )}
      >
        {t('mixed', 'MIXED')}
      </span>
    </button>
  )
}

interface DarkWordRowProps {
  node: PreferenceNode
  onToggle: () => void
}

const DarkWordRow: FC<DarkWordRowProps> = ({ node, onToggle }) => {
  const isMuted = node.preference.skipped
  const occurrenceCount = node.preference.occurrenceTotalCount

  return (
    <button
      type="button"
      className="flex h-[50px] w-full items-center gap-2 border-b border-core-gray-700 py-3 text-left"
      onClick={onToggle}
    >
      {isMuted ? <SpeakerMuteIcon size={24} color="white" /> : <SpeakerMaxIcon size={24} color="white" />}
      <span
        className={classNames(
          'text-[17px] font-semibold leading-none',
          isMuted ? 'text-core-gray-500 line-through' : 'text-white',
        )}
      >
        {node.preference.displayTitle}
        {occurrenceCount != null && occurrenceCount > 0 && (
          <span className="text-core-gray-400"> ({occurrenceCount})</span>
        )}
      </span>
    </button>
  )
}

interface DarkCategoryRowProps {
  node: PreferenceNode
  depth: number
  collapsedIds: Set<number>
  onToggleExpand: (id: number, displayTitle: string) => void
  onMuteToggle: (node: PreferenceNode) => void
}

const DarkCategoryRow: FC<DarkCategoryRowProps> = ({ node, depth, collapsedIds, onToggleExpand, onMuteToggle }) => {
  const isLeaf = node.children.length === 0
  const isExpanded = !collapsedIds.has(node.preference.id)
  const muteStatus = getMuteStatus(node)
  const isRoot = depth === 0
  const occurrenceCount = node.preference.occurrenceTotalCount

  if (isLeaf) {
    return <DarkWordRow node={node} onToggle={() => onMuteToggle(node)} />
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-core-gray-700 py-2">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left"
          onClick={() => onToggleExpand(node.preference.id, node.preference.displayTitle)}
        >
          <TriangleArrowRight
            size={12}
            color={isRoot ? 'oxide-bright' : 'white'}
            className={classNames('shrink-0 transition-transform duration-200 mt-1 mr-1', isExpanded && 'rotate-90')}
          />
          <span
            className={classNames(
              'pt-1 text-[17px] font-semibold leading-[20px]',
              isRoot ? 'text-oxide-bright' : 'text-white',
            )}
          >
            {node.preference.displayTitle}
            {occurrenceCount != null && occurrenceCount > 0 && (
              <span className="text-core-gray-400"> ({occurrenceCount})</span>
            )}
          </span>
        </button>
        <DarkMuteToggle status={muteStatus} onToggle={() => onMuteToggle(node)} />
      </div>
      {isExpanded && (
        <div className={classNames('flex flex-col', depth === 0 ? 'pl-6' : 'pl-8')}>
          {node.children.map((child) => (
            <DarkCategoryRow
              key={child.preference.id}
              node={child}
              depth={depth + 1}
              collapsedIds={collapsedIds}
              onToggleExpand={onToggleExpand}
              onMuteToggle={onMuteToggle}
            />
          ))}
        </div>
      )}
    </>
  )
}

interface ParentalControlsProps {
  isOpen: boolean
  episodeName: string
  preferences: SkipCategoryPreferenceItem[]
  updateSkipPreference: (categoryId: number, skipped: boolean) => Promise<void>
  onSaveAndWatch: () => void
}

export const ParentalControlsModal: React.FC<ParentalControlsProps> = ({
  isOpen,
  episodeName,
  preferences,
  updateSkipPreference,
  onSaveAndWatch,
}) => {
  const isDesktop = useMediaQuery({ minWidth: 768 })
  const { t } = useTranslate('common')
  const track = useSafeTrack()
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set())
  const [mounted, setMounted] = useState(false)
  const wasOpenRef = useRef(false)

  const tree = buildPreferenceTree(preferences)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      track('Parental Controls Modal Opened', { episodeName, source: 'video-modal' })
    }
    wasOpenRef.current = isOpen
  }, [isOpen, episodeName, track])

  const toggleExpanded = (id: number, displayTitle: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      const isCollapsing = !next.has(id)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      track(isCollapsing ? 'Parental Controls Category Collapsed' : 'Parental Controls Category Expanded', {
        categoryName: displayTitle,
        source: 'video-modal',
      })
      return next
    })
  }

  const handleMuteToggle = (node: PreferenceNode) => {
    const isLeaf = node.children.length === 0
    const status = getMuteStatus(node)
    const shouldSkip = status !== 'muted'
    const action = shouldSkip ? 'muted' : 'unmuted'

    if (isLeaf) {
      track('Parental Controls Word Toggled', {
        wordName: node.preference.displayTitle,
        action,
        source: 'video-modal',
      })
    } else {
      track('Parental Controls Category Toggled', {
        categoryName: node.preference.displayTitle,
        action,
        isEntireCategory: true,
        source: 'video-modal',
      })
    }

    updateSkipPreference(node.preference.id, shouldSkip)
  }

  const handleCloseButtonClick = () => {
    track('Parental Controls Modal Closed', { method: 'close-button', source: 'video-modal' })
    onSaveAndWatch()
  }

  const handleBackdropClick = () => {
    track('Parental Controls Modal Closed', { method: 'backdrop-click', source: 'video-modal' })
    onSaveAndWatch()
  }

  const handleSaveAndWatchClick = () => {
    track('Parental Controls Save And Watch Clicked', { source: 'video-modal' })
    onSaveAndWatch()
  }

  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen || !panelRef.current) return

    const panel = panelRef.current
    const previouslyFocused = document.activeElement as HTMLElement | null

    // Focus the panel on open
    panel.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        track('Parental Controls Modal Closed', { method: 'escape-key', source: 'video-modal' })
        onSaveAndWatch()
        return
      }

      if (e.key !== 'Tab') return

      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last?.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocused?.focus()
    }
  }, [isOpen, onSaveAndWatch, track])

  const content = (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between pr-1">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <ParentalControlsFullIcon color="oxide-bright" size={32} />
            <CaptionSM color="core-gray-400" weight="medium">
              {t('parentalControlsFor', 'Parental Controls For:')}
            </CaptionSM>
          </div>
          <TitleMD weight="bold" color="white">
            {episodeName}
          </TitleMD>
          <CaptionSM color="core-gray-500" weight="medium">
            <Translate t={t} i18nKey="parentalControlsBetaLoveYourFeedback">
              {"Parental controls is in beta, we'd love your "}
              <a className="cursor-pointer text-oxide-bright" href={paths.feedback.parentalControls}>
                feedback
              </a>
              .
            </Translate>
          </CaptionSM>
        </div>
        <button
          onClick={handleCloseButtonClick}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20"
          aria-label={t('close', 'Close')}
        >
          <CloseIcon color="white" size={16} />
        </button>
      </div>

      <div className="h-px bg-core-gray-700" />

      <div className="flex flex-col">
        {tree.map((rootNode) => (
          <DarkCategoryRow
            key={rootNode.preference.id}
            node={rootNode}
            depth={0}
            collapsedIds={collapsedIds}
            onToggleExpand={toggleExpanded}
            onMuteToggle={handleMuteToggle}
          />
        ))}
      </div>

      <div className="flex flex-col gap-4">
        <Button variant="white" onClick={handleSaveAndWatchClick} className="h-12 w-full rounded-[14px]">
          <LabelMD weight="semibold" color="core-gray-950">
            {t('saveAndWatch', 'Save & Watch')}
          </LabelMD>
        </Button>
        <CaptionSM color="core-gray-500" weight="medium" className="text-center">
          <Translate t={t} i18nKey="changesSavedToDefaults">
            {'Changes will be saved to your '}
            <a className="cursor-pointer text-oxide-bright" href={paths.account.parentalControls}>
              account defaults
            </a>
            .
          </Translate>
        </CaptionSM>
      </div>
    </div>
  )

  if (!mounted) return null

  return createPortal(
    <>
      <div
        className={classNames(
          'fixed inset-0 z-[100] bg-black/65 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {isDesktop ? (
        <div
          ref={panelRef}
          tabIndex={-1}
          className={classNames(
            'fixed right-0 top-0 z-[101] flex h-full w-[427px] flex-col bg-core-gray-950 outline-none transition-transform duration-300 ease-in-out',
            isOpen ? 'translate-x-0' : 'translate-x-full',
          )}
          role="dialog"
          aria-modal="true"
          aria-label={t('parentalControls', 'Parental Controls')}
        >
          <div className="flex-1 overflow-y-auto px-4 pt-6">{content}</div>
        </div>
      ) : (
        <div
          ref={panelRef}
          tabIndex={-1}
          className={classNames(
            'fixed inset-x-0 bottom-0 z-[101] flex flex-col rounded-t-lg bg-core-gray-950 outline-none transition-transform duration-300 ease-in-out',
            isOpen ? 'translate-y-0' : 'translate-y-full',
          )}
          style={{ maxHeight: '85vh' }}
          role="dialog"
          aria-modal="true"
          aria-label={t('parentalControls', 'Parental Controls')}
        >
          <div className="flex w-full shrink-0 flex-col items-center py-4">
            <div className="h-[5px] w-9 rounded-[2.5px] bg-white/30" />
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4">{content}</div>
        </div>
      )}
    </>,
    document.body,
  )
}
